import { ScheduleItem, SchedulePosition } from "../../interfaces/ISchedules";
import {
  formatTime,
  getDate,
  getFloorPrefix,
  getRowStyle,
  getSchedulePositionStyle,
  hasPositionRounds,
} from "../../utils/scheduleHelpers";
import { getClickableRowStyles, getScheduleTableRowBaseStyles } from "../../utils/scheduleTableStyles";
import { Box, Stack, TableCell, TableRow, Typography, useTheme } from "@mui/material";

interface SchedulePositionTableRowProps {
  position: SchedulePosition;
  positionIndex: number;
  scheduleId: string;
  onPositionClick: (position: SchedulePosition) => void;
}

/** Wiersz tabeli pozycji pojedynczego dnia harmonogramu (port z host-panel). */
export default function SchedulePositionTableRow({
  position,
  positionIndex,
  scheduleId,
  onPositionClick,
}: SchedulePositionTableRowProps) {
  const theme = useTheme();

  const mainItemType = position.items[0]?.type || "OTHER";
  const rowStyle = getRowStyle(mainItemType, theme);
  const positionStatusStyle = getSchedulePositionStyle(position, theme);
  const isClickable = Boolean(position.id && scheduleId && hasPositionRounds(position));

  const handleClick = () => {
    if (isClickable) {
      onPositionClick(position);
    }
  };

  const timeLabel = position.hideStartTime ? "—" : formatTime(getDate(position.plannedStartTime));

  const clickableRowStyles = getClickableRowStyles();
  const rowBaseStyles = getScheduleTableRowBaseStyles(theme);

  return (
    <TableRow
      key={position.id || positionIndex}
      hover={isClickable}
      onClick={handleClick}
      sx={{
        ...rowStyle,
        ...positionStatusStyle,
        ...rowBaseStyles,
        ...(isClickable && clickableRowStyles),
      }}
    >
      <TableCell align="center" sx={{ borderBottom: "none" }}>
        <Typography variant="caption" fontWeight={500} sx={{ textAlign: "center" }}>
          {timeLabel}
        </Typography>
      </TableCell>
      <TableCell sx={{ borderBottom: "none" }}>
        <Stack spacing={0.5}>
          {position.items.map((item: ScheduleItem) => (
            <Box key={item.id}>
              <Typography
                variant="caption"
                fontWeight={item.type === "ROUND" || item.type === "BREAK" || item.type === "CEREMONY" ? 500 : 400}
              >
                {`${getFloorPrefix(item.floorLabel) ? `${getFloorPrefix(item.floorLabel)}: ` : ""}${item.title ?? ""}`}
              </Typography>
              {item.subtitle ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.7rem" }}>
                  {item.subtitle}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ borderBottom: "none" }}>
        <Stack spacing={0.5} alignItems="flex-end">
          {position.items.map((item: ScheduleItem) => {
            const presentationsCount = item.stats?.plannedPresentationsCount ?? 0;
            return presentationsCount > 0 ? (
              <Typography key={item.id} variant="caption" fontWeight={500}>
                {presentationsCount}
              </Typography>
            ) : null;
          })}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
